import { cs } from '../../lib/currency';
import { useEffect, useState } from 'react';
import { useParams, Navigate, useNavigate, Link } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { EmailActivityTab } from '../../components/admin/EmailActivityTab';
import ParentInvoicesPanel from '../../components/admin/ParentInvoicesPanel';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  MapPin,
  DollarSign,
  Receipt,
  Calendar,
  Wallet,
  Loader2,
  Pencil,
  StickyNote,
  ShieldCheck,
  UserX,
  CreditCard,
} from 'lucide-react';
import SaveCardOnFileDialog from '../../components/admin/SaveCardOnFileDialog';
import api from '../../services/api';
import { toast } from 'sonner';
import NetAmount from '../../components/shared/NetAmount';
import SurchargeAmount from '../../components/shared/SurchargeAmount';
import PostManualCreditDialog from '../../components/shared/PostManualCreditDialog';
import SavedPaymentMethodsList from '../../components/admin/SavedPaymentMethodsList';
import { RowAmountFixer } from '../../components/admin/RowAmountFixer';
import TermsBadge from '../../components/shared/TermsBadge';
import CreditLedgerSection from '../../components/admin/CreditLedgerSection';

/**
 * CustomerDetail
 * --------------
 * Lightweight detail page for users whose role is `customer` and who do
 * NOT have any children/enrollments. Surfaces only what's relevant to
 * walk-in lane-booking customers:
 *   - Profile (name/email/phone/address/created_at)
 *   - Lane bookings
 *   - Payments
 *   - Tournament registrations (if any)
 *
 * Deliberately excludes the Children + Enrollments tabs that ParentDetail
 * shows. If the backend reports `should_use_parent_view: true` (the user
 * actually has children), we redirect to /admin/parents/{id}?tab=billing
 * automatically — admin lands on the right page regardless of which link
 * they clicked.
 */
export default function CustomerDetail() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redirectToParent, setRedirectToParent] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [savePmDialog, setSavePmDialog] = useState(false);
  // iter238: inactivate dialog state — customers can be inactivated too.
  const [inactivateDialog, setInactivateDialog] = useState(false);
  const [processingInactivate, setProcessingInactivate] = useState(false);

  // Pulls the customer profile + nested booking/payment/tournament data.
  // Re-callable so per-row fixers can refresh after a manual amount edit.
  const fetchCustomer = async () => {
    try {
      const { data } = await api.get(`/admin/customers/${customerId}`);
      if (data.should_use_parent_view) {
        setRedirectToParent(true);
      } else {
        setCustomer(data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load customer');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomer();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const openEditDialog = () => {
    if (!customer) return;
    setEditForm({
      name: customer.name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      notes: customer.admin_notes || '',
    });
    setEditOpen(true);
  };

  const handleSaveProfile = async () => {
    if (!editForm.name.trim() || !editForm.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/admin/customers/${customerId}`, {
        name: editForm.name,
        email: editForm.email,
        phone: editForm.phone,
        address: editForm.address,
        notes: editForm.notes,
      });
      toast.success('Customer profile updated');
      setEditOpen(false);
      await fetchCustomer();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update customer');
    } finally {
      setSaving(false);
    }
  };

  // iter238: Inactivate a customer. Reuses the parent inactivate endpoint,
  // which is role-agnostic and now staff-accessible. Customers don't have
  // children/enrollments to block on, so the call is a straight POST.
  const handleInactivateCustomer = async () => {
    setProcessingInactivate(true);
    try {
      await api.post(`/parents/${customerId}/inactivate`);
      toast.success(`${customer.name} has been inactivated`);
      setInactivateDialog(false);
      await fetchCustomer();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to inactivate customer');
    } finally {
      setProcessingInactivate(false);
    }
  };

  const handleReactivateCustomer = async () => {
    setProcessingInactivate(true);
    try {
      await api.post(`/parents/${customerId}/reactivate`);
      toast.success(`${customer.name} has been reactivated`);
      await fetchCustomer();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reactivate customer');
    } finally {
      setProcessingInactivate(false);
    }
  };

  // iter387 — Copy a no-login, non-expiring public payment link covering the
  // customer's whole outstanding balance (works for inactive customers too).
  const [copyingPayLink, setCopyingPayLink] = useState(false);
  const handleCopyPayLink = async () => {
    setCopyingPayLink(true);
    try {
      const { data } = await api.get(`/admin/parents/${customerId}/pay-link`);
      if (!data?.total_outstanding || data.total_outstanding <= 0) {
        toast.info('No outstanding balance — nothing to collect.');
        return;
      }
      try {
        await navigator.clipboard.writeText(data.pay_url);
        toast.success(`Payment link copied — ${cs()}${Number(data.total_outstanding).toFixed(2)} across ${data.invoice_count} invoice(s). Share it; no login needed.`);
      } catch {
        window.prompt('Copy this payment link:', data.pay_url);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payment link');
    } finally {
      setCopyingPayLink(false);
    }
  };

  if (redirectToParent) {
    return <Navigate to={`/admin/parents/${customerId}?tab=billing`} replace />;
  }

  if (loading) {
    return (
      <DashboardLayout title="Customer">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  if (!customer) {
    return (
      <DashboardLayout title="Customer">
        <div className="text-center py-12 text-slate-500">Customer not found.</div>
      </DashboardLayout>
    );
  }

  const totals = customer.totals || {};
  const fmt = (v) => `${cs()}${Number(v || 0).toFixed(2)}`;

  return (
    <DashboardLayout title={customer.name || 'Customer'}>
      <div className="space-y-6" data-testid="customer-detail-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-slate-600"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <TermsBadge
              termsAcceptedAt={customer.terms_accepted_at}
              termsLastAcceptedAt={customer.terms_last_accepted_at}
              termsSource={customer.terms_source}
              testId="customer-terms-badge"
            />
            <Badge variant="outline" className="text-xs uppercase tracking-wide">
              {customer.role || 'customer'}
            </Badge>
          </div>
        </div>

        {/* Profile card */}
        <Card data-testid="customer-profile-card">
          <CardHeader>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5 text-blue-600" />
                {customer.name}
              </CardTitle>
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSavePmDialog(true)}
                  data-testid="save-payment-method-btn"
                  className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                >
                  <ShieldCheck className="h-4 w-4 mr-1" />
                  Save Card / ACH
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyPayLink}
                  disabled={copyingPayLink}
                  data-testid="copy-customer-pay-link-btn"
                  title="Copy a no-login link to pay the full outstanding balance (works for inactive customers)"
                >
                  <CreditCard className="h-4 w-4 mr-1" />
                  {copyingPayLink ? 'Preparing…' : 'Copy Payment Link'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openEditDialog}
                  data-testid="edit-customer-btn"
                >
                  <Pencil className="h-4 w-4 mr-1" />
                  Edit Profile
                </Button>
                {/* iter239: post manual credit (admin + staff). */}
                <PostManualCreditDialog
                  userId={customer.id}
                  userName={customer.name}
                  userRole="customer"
                  onPosted={fetchCustomer}
                />
                {/* iter238: Inactivate / Reactivate button. Customer
                    role doesn't need the children-check shown on parents
                    — there are no enrollments to block on.
                    iter258 — read `is_active` (bool) instead of the
                    legacy `status` string. */}
                {customer.is_active === false ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleReactivateCustomer}
                    disabled={processingInactivate}
                    data-testid="reactivate-customer-btn"
                    className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                  >
                    {processingInactivate ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <ShieldCheck className="h-4 w-4 mr-1" />
                    )}
                    Reactivate
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setInactivateDialog(true)}
                    data-testid="inactivate-customer-btn"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <UserX className="h-4 w-4 mr-1" />
                    Inactivate
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2 text-slate-700">
              <Mail className="h-4 w-4 text-slate-400" />
              <span data-testid="customer-email">{customer.email || '—'}</span>
            </div>
            <div className="flex items-center gap-2 text-slate-700">
              <Phone className="h-4 w-4 text-slate-400" />
              <span data-testid="customer-phone">{customer.phone || '—'}</span>
            </div>
            {customer.address && (
              <div className="flex items-center gap-2 text-slate-700 sm:col-span-2">
                <MapPin className="h-4 w-4 text-slate-400" />
                <span data-testid="customer-address">{customer.address}</span>
              </div>
            )}
            {customer.admin_notes && (
              <div className="flex items-start gap-2 text-slate-700 sm:col-span-2 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                <StickyNote className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <span
                  className="text-xs whitespace-pre-line"
                  data-testid="customer-admin-notes"
                >
                  {customer.admin_notes}
                </span>
              </div>
            )}
            {customer.created_at && (
              <div className="flex items-center gap-2 text-slate-500 text-xs sm:col-span-2">
                <Calendar className="h-3.5 w-3.5" />
                Joined {new Date(customer.created_at).toLocaleDateString()}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Totals strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-500">Total Paid</p>
                <DollarSign className="h-4 w-4 text-emerald-500" />
              </div>
              <p
                className="text-2xl font-bold text-emerald-700"
                data-testid="customer-total-paid"
              >
                {fmt(totals.total_paid)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-500">Outstanding</p>
                <Receipt className="h-4 w-4 text-amber-500" />
              </div>
              <p
                className="text-2xl font-bold text-amber-700"
                data-testid="customer-outstanding"
              >
                {fmt(totals.outstanding)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-500">Lane Bookings</p>
                <Calendar className="h-4 w-4 text-blue-500" />
              </div>
              <p className="text-2xl font-bold text-blue-700">
                {customer.lane_bookings?.length || 0}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-500">Credit Balance</p>
                <Wallet className="h-4 w-4 text-violet-500" />
              </div>
              <p className="text-2xl font-bold text-violet-700">
                {fmt(customer.credit_balance)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* iter239: Pending manual invoices — admin-created invoices for
            this customer that aren't auto-generated by the enrollment
            cycle. Visible to admins, staff, parents and customers. */}
        {(customer.pending_manual_invoices?.length || 0) > 0 && (
          <Card data-testid="customer-pending-invoices-card" className="border-amber-200 bg-amber-50/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-900">
                <Receipt className="h-5 w-5" />
                Pending Invoices
                <Badge variant="outline" className="ml-2 text-amber-700 border-amber-300 bg-amber-100">
                  {customer.pending_manual_invoices.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Description</TableHead>
                    <TableHead>Due</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customer.pending_manual_invoices.map((inv) => (
                    <TableRow
                      key={inv.id}
                      data-testid={`customer-manual-invoice-row-${inv.id}`}
                    >
                      <TableCell className="font-medium">{inv.description}</TableCell>
                      <TableCell>{inv.due_date || '—'}</TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : '—'}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {fmt(inv.amount)}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={inv.status} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* iter241: Saved Payment Methods (live from Stripe). */}
        <Card data-testid="customer-saved-pm-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-indigo-600" />
              Saved Payment Methods
            </CardTitle>
          </CardHeader>
          <CardContent>
            <SavedPaymentMethodsList userId={customer.id} onChanged={fetchCustomer} />
          </CardContent>
        </Card>

        {/* Tabs — Lane Bookings + Payments. Deliberately NO Children /
            Enrollments tabs (this is the customer-only view; that's the
            whole point of the page per the user request). */}
        <Tabs defaultValue="invoices" className="w-full">
          <TabsList>
            <TabsTrigger value="invoices" data-testid="tab-invoices">
              Invoices &amp; Billing
            </TabsTrigger>
            <TabsTrigger value="credit" data-testid="tab-credit">
              Account Credit
            </TabsTrigger>
            {(customer.tournament_registrations?.length || 0) > 0 && (
              <TabsTrigger value="tournaments" data-testid="tab-tournaments">
                Tournaments ({customer.tournament_registrations.length})
              </TabsTrigger>
            )}
            <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
          </TabsList>

          {/* iter371 — Full invoice management parity with the parent view:
              view / pay / charge / edit / resend / void / refund / mark-paid
              / mark-settled — the same ParentInvoicesPanel parents get. */}
          <TabsContent value="invoices" className="mt-3">
            <ParentInvoicesPanel
              parentId={customer.id}
              parentEmail={customer.email}
              defaultStatus="all"
            />
          </TabsContent>


          <TabsContent value="credit" className="mt-3">
            {/* Account credit ledger — the one piece not already shown in the
                unified Invoices & Billing panel above. Lane Bookings and the
                Payments table were removed as duplicates of the invoice list. */}
            <CreditLedgerSection userId={customerId} />
          </TabsContent>

          {(customer.tournament_registrations?.length || 0) > 0 && (
            <TabsContent value="tournaments" className="mt-3">
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tournament</TableHead>
                        <TableHead>Player</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {customer.tournament_registrations.map((t) => (
                        <TableRow key={t.id}>
                          <TableCell>{t.tournament_name || t.tournament_id}</TableCell>
                          <TableCell>{t.player_name || '—'}</TableCell>
                          <TableCell className="text-right font-medium">
                            <div className="flex items-center justify-end gap-2 flex-wrap">
                              <NetAmount
                                amount={t.payment_amount}
                                refundedAmount={t.refunded_amount}
                                refundStatus={t.refund_status}
                                testId={`customer-tournament-net-${t.id}`}
                              />
                              {t.payment_status === 'paid'
                                && (!t.payment_amount || Number(t.payment_amount) <= 0) && (
                                <RowAmountFixer
                                  entityType="tournament_registration"
                                  entityId={t.id}
                                  currentAmount={t.payment_amount}
                                  onUpdated={fetchCustomer}
                                  compact
                                />
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={t.payment_status} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* iter252 — Email Activity tab */}
          <TabsContent value="activity" className="mt-3">
            <EmailActivityTab userId={customer.id} />
          </TabsContent>
        </Tabs>

        {/* Quick link to billing-management filtered to this customer */}
        <div className="text-right">
          <Link
            to={`/admin/billing-management?customer=${customer.id}`}
            className="text-sm text-blue-600 hover:underline"
            data-testid="open-billing-management-link"
          >
            Open in Billing Management →
          </Link>
        </div>

        {/* Edit Profile Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="max-w-md" data-testid="edit-customer-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <Pencil className="h-5 w-5 text-blue-600" />
                Edit Customer Profile
              </DialogTitle>
              <DialogDescription>
                Update contact information for this customer. Changes are
                logged to the audit trail.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <div className="space-y-1">
                <Label htmlFor="edit-name">
                  Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="edit-name"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  data-testid="edit-customer-name"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit-email">
                  Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  data-testid="edit-customer-email"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit-phone">Phone</Label>
                <Input
                  id="edit-phone"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  placeholder="555-1234"
                  data-testid="edit-customer-phone"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit-address">Address</Label>
                <Input
                  id="edit-address"
                  value={editForm.address}
                  onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                  placeholder="123 Main St, Springfield"
                  data-testid="edit-customer-address"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit-notes">Admin Notes</Label>
                <Textarea
                  id="edit-notes"
                  rows={3}
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  placeholder="Internal notes (only visible to admins & staff)"
                  data-testid="edit-customer-notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setEditOpen(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveProfile}
                disabled={saving}
                data-testid="save-customer-btn"
              >
                {saving ? 'Saving…' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Save payment method dialog (card / ACH) */}
        <SaveCardOnFileDialog
          open={savePmDialog}
          onOpenChange={setSavePmDialog}
          parent={{ id: customer.id, name: customer.name, email: customer.email }}
          onSaved={() => { /* nothing to refresh on this page */ }}
        />

        {/* iter238: Inactivate confirmation dialog */}
        <Dialog open={inactivateDialog} onOpenChange={setInactivateDialog}>
          <DialogContent data-testid="inactivate-customer-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <UserX className="h-5 w-5" />
                Inactivate Customer Account
              </DialogTitle>
              <DialogDescription>
                {customer.name} ({customer.email})
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-900">
                <p className="font-medium">Are you sure you want to inactivate this customer?</p>
                <ul className="list-disc list-inside mt-2 text-xs space-y-1 text-amber-800">
                  <li>The customer will no longer be able to log in or book lanes.</li>
                  <li>Existing bookings, payments and tournament history stay intact.</li>
                  <li>You can reactivate the customer later from this same page.</li>
                </ul>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setInactivateDialog(false)}
                disabled={processingInactivate}
                data-testid="cancel-inactivate-btn"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleInactivateCustomer}
                disabled={processingInactivate}
                data-testid="confirm-inactivate-btn"
              >
                {processingInactivate ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" /> Inactivating…
                  </>
                ) : (
                  'Inactivate Customer'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

function StatusBadge({ status }) {
  const v = String(status || '').toLowerCase();
  const cls =
    v === 'paid' || v === 'completed' || v === 'succeeded'
      ? 'bg-emerald-100 text-emerald-700'
      : v === 'pending'
        ? 'bg-amber-100 text-amber-700'
        : v === 'unpaid'
          ? 'bg-orange-100 text-orange-700'
          : v === 'overdue'
            ? 'bg-red-100 text-red-700'
            : v === 'refunded' || v === 'reversed'
              ? 'bg-slate-200 text-slate-600'
              : 'bg-slate-100 text-slate-600';
  return (
    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${cls}`}>
      {status || '—'}
    </span>
  );
}
